import { Link } from '@tanstack/react-router'
import { Logo } from './Logo.js'
import { StatusPill } from './StatusPill.js'

/**
 * Full-page lockout shown when the signed-in admin's academy is suspended or
 * cancelled. The API already rejects every academy-scoped route with
 * ACADEMY_SUSPENDED; this surfaces the state calmly and points the owner at
 * billing, the one place still reachable so they can pay to reactivate.
 *
 * `onSignOut` lets a locked-out user leave the session (e.g. to switch to
 * another account) — otherwise the lockout screen would trap them with no exit.
 */
export function AcademySuspended({
  status,
  onSignOut,
}: {
  status: string
  onSignOut?: () => void
}) {
  const cancelled = status === 'cancelled'
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-surface-muted px-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-8 shadow-sm text-center">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        <div className="flex justify-center mb-4">
          <StatusPill tone={status}>{status}</StatusPill>
        </div>
        <h1 className="text-lg font-semibold text-fg">
          {cancelled ? 'Your academy is cancelled' : 'Your academy is suspended'}
        </h1>
        <p className="mt-2 text-sm text-fg-muted">
          {cancelled
            ? 'Access to the dashboard is paused. To reopen your academy, renew your subscription or get in touch with support.'
            : 'Access to the dashboard is paused, usually because of a billing issue. Update your subscription to restore access right away.'}
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <Link
            to="/billing"
            className="inline-flex items-center justify-center h-10 px-4 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] shadow-sm"
          >
            Go to subscription
          </Link>
          <a
            href="mailto:support@brightstars.in"
            className="text-xs text-fg-muted hover:text-fg transition-colors"
          >
            Contact support
          </a>
          {onSignOut ? (
            <button
              type="button"
              onClick={onSignOut}
              className="text-xs text-fg-muted hover:text-fg transition-colors"
            >
              Sign out
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
